import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import { Confetti } from "@/components/Fx";

export default function Businesses() {
  const { setCharacter } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [burst, setBurst] = useState(0);

  const load = useCallback(async () => {
    try { const r = await api.get("/businesses"); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (path, id, label) => {
    setBusy(id + path);
    try {
      const { data: res } = await api.post(`/businesses/${path}`, { business_id: id });
      setCharacter(res.character);
      if (res.collected !== undefined) { setBurst((b) => b + 1); toast.success(`Collected +${naira(res.collected)} 💰`); }
      else toast.success(label);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) return null;

  return (
    <div className="px-5 pt-6" data-testid="businesses-page">
      <Confetti burst={burst} />
      <div className="flex items-center gap-3">
        <button data-testid="biz-back" onClick={() => nav("/city")} className="p-2 rounded-full bg-slate-100"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl">My <span className="text-[#FFD700]">Hustles</span></h1>
          <p className="label-xs text-slate-500">Own businesses, collect passive income, restock to keep earning</p>
        </div>
      </div>
      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between">
        <span className="label-xs text-slate-500">Wallet</span>
        <span className="text-[#16A34A] font-display" data-testid="biz-wallet">{naira(data.wallet)}</span>
      </div>

      <div className="mt-4 space-y-3 mb-4">
        {data.businesses.map((b) => {
          const Icon = Icons[b.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.Store;
          const stockPct = b.owned ? Math.round((b.stock / b.max_stock) * 100) : 0;
          return (
            <motion.div key={b.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`biz-card-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 flex items-center justify-center text-2xl">{b.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">{b.title} {b.owned && <span className="label-xs text-[#16A34A]">OWNED</span>}</div>
                  <div className="label-xs text-slate-500 leading-tight">{b.desc}</div>
                </div>
              </div>

              {b.owned ? (
                <>
                  <div className="mt-3 flex items-center justify-between label-xs">
                    <span className="text-slate-500">Stock buffer</span>
                    <span className="text-slate-500">{naira(b.stock)} / {naira(b.max_stock)}</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-black/60 overflow-hidden">
                    <div className="h-full rounded-full bg-[#FFD700]" style={{ width: `${stockPct}%` }} />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="label-xs text-slate-500">Ready to collect</span>
                    <span className="text-[#16A34A] font-display" data-testid={`biz-accrued-${b.id}`}>{naira(b.accrued)}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button data-testid={`biz-collect-${b.id}`} disabled={busy === b.id + "collect"} onClick={() => act("collect", b.id, "Collected")}
                      className="bg-[#16A34A] text-white font-bold py-2.5 rounded-lg label-xs disabled:opacity-40">Collect</button>
                    <button data-testid={`biz-restock-${b.id}`} disabled={busy === b.id + "restock"} onClick={() => act("restock", b.id, "Restocked ✓")}
                      className="bg-slate-100 border border-[#FFD700]/40 text-[#FFD700] font-bold py-2.5 rounded-lg label-xs disabled:opacity-40">Restock · {naira(b.restock_cost)}</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <Stat label="Price" value={naira(b.price)} />
                    <Stat label="Per hour" value={naira(b.income_per_hour)} accent />
                    <Stat label="Clout" value={`+${b.clout}`} />
                  </div>
                  <button data-testid={`biz-buy-${b.id}`} disabled={b.locked || busy === b.id + "buy"} onClick={() => act("buy", b.id, "Business opened 🎉")}
                    className="mt-3 w-full py-3 rounded-xl font-bold label-xs bg-[#FFD700] text-slate-900 disabled:opacity-40 flex items-center justify-center gap-2">
                    {b.locked ? <><Lock size={14} /> Unlocks at Lv{b.min_level}</> : <>Buy for {naira(b.price)}</>}
                  </button>
                </>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-slate-100 rounded-lg py-2">
      <div className="label-xs text-slate-500">{label}</div>
      <div className={`text-sm font-semibold ${accent ? "text-[#16A34A]" : ""}`}>{value}</div>
    </div>
  );
}
